import * as React from 'react';
import { Formatter } from '@fnv-core/formatting';
import { Tokenizer } from '@fnv-core/tokens';

interface IColumn {
  title: string;
  property: string;
  template?: string;
  formatter?: string;
  formatterOptions?: any;
}

interface IListProps {
  /**
   * A unique name to add to the container class.
   */
  name: string;
  /**
   * An optional title to display.
   */
  title?: string;
  /**
   * Text to display when there are no items.
   */
  noDataText?: string;
  /**
   * The items to display.
   */
  items: any[];
  /**
   * The columns to display.
   */
  columns: IColumn[];
}

/**
 * A component for displaying items in a list style.
 */
export const List = (props: IListProps) => {

  const { items, name, title, columns, noDataText } = props;

  if (!items) {
    return null;
  }

  return (
    <div className={`formfield-list formfield-list-${name} text-l`}>
      {title && <h3 className="widget__title">{title}</h3>}
      {!items.length && <span className="formfield-list__no-items-text">{noDataText}</span>}
      {!!items.length && (
        <div className="formfield-list__items">
          {items.map((item) => (
            <div key={item} className="formfield-list__list-item mb-3">
              <ListItem data={item} columns={columns} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface IListItemProps {
  data: {
    [key: string]: string;
  };
  columns: IColumn[];
}

const ListItem = ({ data, columns }: IListItemProps) => {

  const getDisplayValue = (column: IColumn) => {

    let value = data[column.property];
    let displayValue;
    if (column.formatter) {
      displayValue = Formatter.format(value, column.formatter, column.formatterOptions);
    } else {
      displayValue = value;
    }

    if (!column.template) {
      return displayValue;
    }

    // Pass other values as well so it can be used in the template, e.g.:
    // Value is {value} and another thing is {AnotherProperty}
    const otherTokens = Object.keys(data).map(key => {
      return {
        name: key,
        value: data[key].toString(),
      };
    });
    const tokens = [
      ...otherTokens,
      { name: 'value', value: displayValue },
    ];

    const tokenized = Tokenizer.replaceTokens(column.template, tokens, true);
    return tokenized;
  };

  return (
    <>
      {columns.map((column) => {
        const displayValue = getDisplayValue(column);
        return (
          <div key={column.title} className="row">
            <div className="col-12 col-sm-6 col-lg-3 formfield-list__label">{column.title}</div>
            <div className="col-12 col-sm-6 col-lg-9 formfield-list__value">{displayValue}</div>
          </div>
          );
        })
      }
    </>
  );
};
